// Layer 3 (Transformation): compare a workbook-shaped FMEA row with its
// session learning override. No state mutation or React lives here.

import type { CaseStopPlan, FailureMode } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import {
  fmeaChangeFor,
  fmeaLearningRow,
  type FmeaLearningProposalInput,
  type FmeaLearningRowState,
  type FmeaLearningSessionState,
} from '@/versions/v1/shared/FmeaLearningEngine';
import { DRIVER_TREE_SEED } from '@/versions/v1/shared/bunker-hill-driver-tree-seed';
import { RISK_ENTRIES } from '@/versions/v1/modules/risk/seed';
import { pluralCount } from '@/versions/v1/shared/text.derive';

export type FmeaLearningCell = {
  value: string;
  previous?: string;
  changed: boolean;
  /** False while the change is a draft proposal (D19): the diff reads, but the row is not live. */
  live?: boolean;
};

export type FmeaLearningRead = {
  failureModeId: string;
  failureMode: string;
  cause: string;
  severity: FmeaLearningCell;
  occurrence: FmeaLearningCell;
  detection: FmeaLearningCell;
  rpn: FmeaLearningCell;
  lastSeen: FmeaLearningCell;
  earlyWarning: FmeaLearningCell;
  evidence: FmeaLearningCell;
  /** K13c: the cause the row carries, drawn under the cause column. */
  learnedCause?: string;
  /**
   * K13c r2: true when this change adds the line. False when the live row
   * already carried it — the proposal inherited it, so it reads as part of the
   * row, never as a new "+" addition.
   */
  learnedCauseAdded?: boolean;
  /** K13c: the early warning the row carries, drawn under it. */
  learnedEarlyWarning?: string;
  /** K13c r2: see `learnedCauseAdded`. */
  learnedEarlyWarningAdded?: boolean;
  updatedLine?: string;
};

/** `07 Jun` — the FMEA row reads day and month only, as the comp does (the year is the plant's current one). */
function dateLabel(date: string | null): string {
  if (!date) return '';
  const [year, month, day] = date.slice(0, 10).split('-').map(Number);
  const stamp = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1, 12));
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  }).format(stamp);
}

function cell(value: string, previous: string | undefined, live = true): FmeaLearningCell {
  return previous !== undefined && previous !== value
    ? { value, previous, changed: true, live }
    : { value, changed: false };
}

function rowCells(
  mode: FailureMode,
  row: FmeaLearningRowState,
  before: FmeaLearningRowState | undefined,
  live: boolean,
): Omit<FmeaLearningRead, 'updatedLine'> {
  // K13c r2: a learned line is a "+" addition only when this change introduces
  // it. `fmeaChangeFor` inherits the live row's lesson when the proposal carries
  // none, so comparing against `before` stops a live lesson re-reading as new.
  const learnedCauseAdded = row.learnedCause !== undefined && row.learnedCause !== before?.learnedCause;
  const learnedEarlyWarningAdded = row.learnedEarlyWarning !== undefined
    && row.learnedEarlyWarning !== before?.learnedEarlyWarning;
  return {
    failureModeId: mode.id,
    failureMode: mode.failureMode,
    cause: mode.cause,
    severity: cell(String(row.s), before ? String(before.s) : undefined, live),
    occurrence: cell(String(row.o), before ? String(before.o) : undefined, live),
    detection: cell(String(row.d), before ? String(before.d) : undefined, live),
    rpn: cell(String(row.rpn), before ? String(before.rpn) : undefined, live),
    lastSeen: cell(dateLabel(row.lastSeen), snapshotDate(before), live),
    earlyWarning: cell(row.earlyWarning, before?.earlyWarning, live),
    evidence: cell(pluralCount(row.evidenceCount, 'case'), before ? pluralCount(before.evidenceCount, 'case') : undefined, live),
    ...(row.learnedCause ? { learnedCause: row.learnedCause, learnedCauseAdded } : {}),
    ...(row.learnedEarlyWarning ? { learnedEarlyWarning: row.learnedEarlyWarning, learnedEarlyWarningAdded } : {}),
  };
}

function snapshotDate(row: FmeaLearningRowState | undefined): string | undefined {
  return row?.lastSeen ? dateLabel(row.lastSeen) : undefined;
}

/** Distinct session-updated rows in approval order; the UI receives labels only. */
export function fmeaLearningModes(state: FmeaLearningSessionState): FailureMode[] {
  const ids = [...new Set([...state.approvals.values()].map((row) => row.failureModeId))];
  return ids.flatMap((id) => FAILURE_MODES.filter((mode) => mode.id === id));
}

/** The newest approval for this failure mode supplies its in-place diff. */
export function fmeaLearningRead(
  mode: FailureMode,
  state: FmeaLearningSessionState,
): FmeaLearningRead {
  const row = fmeaLearningRow(state, mode);
  const approval = [...state.approvals.values()]
    .reverse()
    .find((entry) => entry.failureModeId === mode.id);
  return {
    ...rowCells(mode, row, approval?.before, true),
    updatedLine: approval
      ? `Updated ${approval.updatedAt.slice(11, 16)} from ${approval.caseCode} by ${approval.updatedBy}`
      : undefined,
  };
}

/**
 * D19 phases of one case's FMEA change on the case page.
 * - `draft`: the recommendation proposes a change; the action is not approved yet.
 * - `proposed`: the action is approved; the change waits on the Senior Metallurgist seat.
 * - `live`: the seat approved it; the row carries the change.
 * - `none`: nothing to propose yet (before the diagnosis lands).
 */
export type FmeaCasePhase = 'none' | 'draft' | 'proposed' | 'live';

export type FmeaCaseRead = {
  phase: FmeaCasePhase;
  row: FmeaLearningRead;
  /** `Draft — not live until approved` or `Approved · hh:mm · <seat>`. */
  pill?: { text: string; tone: 'draft' | 'approved' };
  /** The seat chip beside the gate: holder · seat (`GM · Senior met seat`). */
  seatLabel: string;
  /** The gate's action for this persona. */
  gate: 'approve' | 'await-action' | 'await-seat' | 'approved' | 'none';
  /** K13d: who reads this failure mode, derived from the seeds (never typed). */
  readers: FmeaReadersRead;
  /** K13d: the planned stop's forward line, while the inspection is still ahead. */
  nextStep?: FmeaNextStepRead;
};

export const FMEA_PROPOSAL_SEAT = 'Senior metallurgist';

// ---------------------------------------------------------------------------
// K13d: the row's two seeded notes — who reads it, and what its stop will teach.
// ---------------------------------------------------------------------------

/** The surfaces that read one failure mode, read off the seeds. */
export type FmeaReadersRead = {
  /** Risk-register row ids whose own FMEA link is this failure mode (`RK-02`). */
  riskIds: readonly string[];
  /** True when the driver tree holds an issue linked to the case this row is read for. */
  driverTree: boolean;
  /** `RK-02 and the driver tree read this row`; undefined when nothing reads it. */
  note?: string;
};

/** The driver tree reads a row through the issue it holds on that case. */
const DRIVER_TREE_READER = 'the driver tree';
const READERS_VERB = { one: 'reads this row', many: 'read this row' } as const;

function joinReaders(readers: readonly string[]): string {
  if (readers.length <= 1) return readers[0] ?? '';
  if (readers.length === 2) return `${readers[0]} and ${readers[1]}`;
  return `${readers.slice(0, -1).join(', ')} and ${readers[readers.length - 1]}`;
}

/**
 * K13d: who reads this FMEA row. The risk register contributes the rows whose
 * own `failureModeId` is this mode; the driver tree contributes itself when it
 * carries an issue linked to the case the row is read for. Both are seed joins,
 * so no reader name is ever typed on the glass.
 */
export function fmeaReadersRead(mode: FailureMode, caseId?: string): FmeaReadersRead {
  const riskIds = RISK_ENTRIES.filter((row) => row.failureModeId === mode.id).map((row) => row.id);
  const driverTree = caseId !== undefined
    && DRIVER_TREE_SEED.issues.some((issue) => issue.caseId === caseId);
  const readers = [...riskIds, ...(driverTree ? [DRIVER_TREE_READER] : [])];
  if (readers.length === 0) return { riskIds, driverTree };
  return {
    riskIds,
    driverTree,
    note: `${joinReaders(readers)} ${readers.length === 1 ? READERS_VERB.one : READERS_VERB.many}`,
  };
}

/** The forward line under the row: the stop's confirming step and the rating it moves. */
export type FmeaNextStepRead = {
  /** `Cause confirmed at inspection`. */
  lead: string;
  /** The engine's occurrence rating letter, `O`. */
  rating: string;
  /** `updates`. */
  suffix: string;
  /** The whole line, for probes and rendering; `Cause confirmed at inspection → O updates`. */
  line: string;
};

/**
 * The rating the FMEA learning engine moves on approval. `fmeaChangeFor` carries
 * the occurrence tally forward and recomputes S × O × D, so O is the cell the
 * row's next stop will change — the letter is read from the engine's own field.
 */
const RATING_MOVED_ON_APPROVAL: keyof Pick<FmeaLearningRowState, 's' | 'o' | 'd'> = 'o';
const CONFIRMING_STEP = 'inspection';
const NEXT_STEP_SUFFIX = 'updates';

/**
 * K13d: the row promises what the planned stop will teach it — the inspection
 * confirms the cause and the occurrence rating updates. It shows only for a
 * controlled-stop case whose inspection is still ahead at the cursor, and never
 * once the change is live: by then O has already moved.
 */
export function fmeaNextStepRead(input: {
  /** The case's controlled stop plan; absent on every other case. */
  stopPlan?: CaseStopPlan;
  /** The replay cursor; the inspection must still be ahead of it. */
  cursor?: SeriesTime;
  phase: FmeaCasePhase;
}): FmeaNextStepRead | undefined {
  const stopAt = input.stopPlan?.window.at;
  const inspectionAhead = stopAt !== undefined
    && input.cursor !== undefined
    && input.cursor < stopAt;
  if (!inspectionAhead || input.phase === 'live') return undefined;
  const lead = `Cause confirmed at ${CONFIRMING_STEP}`;
  const rating = RATING_MOVED_ON_APPROVAL.toUpperCase();
  return { lead, rating, suffix: NEXT_STEP_SUFFIX, line: `${lead} → ${rating} ${NEXT_STEP_SUFFIX}` };
}

/**
 * K13b: one case's FMEA phase, read from the engine's own maps without a
 * failure-mode context. `draft` is a projection of the recommendation, so it is
 * never stored; a case that only has a preview reads as `none` here.
 */
export function fmeaCasePhase(
  state: FmeaLearningSessionState | undefined,
  caseId: string,
): FmeaCasePhase {
  if (state?.approvals.has(caseId)) return 'live';
  if (state?.proposals.has(caseId)) return 'proposed';
  return 'none';
}

/** What the case page may show as a D19 draft for one case. */
export type FmeaCasePreview = Pick<FmeaLearningProposalInput,
  'excursionDate' | 'earlyWarning' | 'learnedCause' | 'learnedEarlyWarning'>;

/**
 * K13d: the case page's draft preview. The FMEA draft belongs to the Action
 * stage — the row shows nothing before the shared stage read reaches Action (the
 * W3d review caught the bearing drafting at 08 Jun 03:15, before Cases). Once
 * Action is current, a controlled-stop case drafts from its stop plan alone,
 * while every other case drafts from the recommendation at the cursor, whose
 * early warning and seeded lesson ride along.
 */
export function fmeaCasePreview(input: {
  /** True once Action is the current stage at the replay clock. */
  actionReached: boolean;
  /** The case's raised clock; the draft's new last-seen. */
  excursionDate: string;
  /** A `stopPlan` case drafts as soon as Action is reached. */
  controlledStop: boolean;
  /** True when the case's recommendation is at the cursor. */
  hasRecommendation: boolean;
  earlyWarning?: string;
  learnedCause?: string;
  learnedEarlyWarning?: string;
}): FmeaCasePreview | undefined {
  if (!input.actionReached) return undefined;
  if (input.controlledStop) return { excursionDate: input.excursionDate };
  if (!input.hasRecommendation) return undefined;
  return {
    excursionDate: input.excursionDate,
    earlyWarning: input.earlyWarning,
    learnedCause: input.learnedCause,
    learnedEarlyWarning: input.learnedEarlyWarning,
  };
}

/** One case's living-FMEA row and its second gate, for the persona holding (or not holding) the seat. */
export function fmeaCaseRead(input: {
  mode: FailureMode;
  state: FmeaLearningSessionState;
  caseId: string;
  /** What the recommendation would change, once the diagnosis has landed at the cursor. */
  preview?: Pick<FmeaLearningProposalInput, 'excursionDate' | 'earlyWarning' | 'learnedCause' | 'learnedEarlyWarning'>;
  seatHolderLabel: string;
  canApprove: boolean;
  /** K13d: the case's controlled stop plan; drives the next-step line. */
  stopPlan?: CaseStopPlan;
  /** K13d: the replay cursor; the stop's inspection must still be ahead of it. */
  cursor?: SeriesTime;
}): FmeaCaseRead {
  const { mode, state, caseId, preview, seatHolderLabel, canApprove, stopPlan, cursor } = input;
  const seatLabel = `${seatHolderLabel} · Senior met seat`;
  const readers = fmeaReadersRead(mode, caseId);
  const withNotes = <T extends Omit<FmeaCaseRead, 'readers' | 'nextStep'>>(read: T): FmeaCaseRead => ({
    ...read,
    readers,
    nextStep: fmeaNextStepRead({ stopPlan, cursor, phase: read.phase }),
  });
  const approval = state.approvals.get(caseId);
  if (approval && approval.failureModeId === mode.id) {
    return withNotes({
      phase: 'live',
      row: {
        ...rowCells(mode, approval.after, approval.before, true),
        updatedLine: `Updated ${approval.updatedAt.slice(11, 16)} from ${approval.caseCode} by ${approval.updatedBy}`,
      },
      pill: { text: `Approved · ${approval.updatedAt.slice(11, 16)} · ${approval.updatedBy}`, tone: 'approved' },
      seatLabel,
      gate: 'approved',
    });
  }
  const proposal = state.proposals.get(caseId);
  const draftInput = proposal && proposal.failureModeId === mode.id ? proposal : preview;
  if (!draftInput) {
    return withNotes({ phase: 'none', row: fmeaLearningRead(mode, state), seatLabel, gate: 'none' });
  }
  const change = fmeaChangeFor(state, { failureMode: mode, ...draftInput });
  const phase: FmeaCasePhase = proposal ? 'proposed' : 'draft';
  return withNotes({
    phase,
    row: rowCells(mode, change.after, change.before, false),
    pill: { text: 'Draft — not live until approved', tone: 'draft' },
    seatLabel,
    gate: !canApprove ? 'await-seat' : phase === 'proposed' ? 'approve' : 'await-action',
  });
}
